import { useCallback, useEffect, useRef, useState } from "react";
import { emitSessionEndAndSettle } from "../state/sessionEndSignal";
import { addBreadcrumb } from "../config/sentry";
import { markTeardown } from "../utils/teardownState";
import {
  ACTIVITY_EVENTS,
  ACTIVITY_THROTTLE_MS,
  AUTH_TOKEN_KEY,
  SESSION_DEADLINE_KEY,
  SESSION_EXPIRY_CLAIM_KEY,
  SESSION_EXPIRY_CLAIM_LEASE_MS,
  SESSION_DEADLINE_LOCK,
  SESSION_IDLE_MS,
  SESSION_END_REASON_SESSION_ENDED,
  TICK_MS,
  isWarning as isWithinWarning,
  markIdleLogout,
  markSessionEndReasonIfUnset,
} from "../config/sessionTimeout";

/**
 * GMA-1909 — the idle-session countdown.
 *
 * Owns one deadline, shared across tabs through localStorage, and calls
 * `onExpire` exactly once when it passes.
 *
 * Deliberately imports nothing from the API layer. Rendering the countdown
 * must never issue an authenticated request, or the timer would keep alive the
 * very session it exists to end.
 *
 * localStorage is accessed through guards for the same reason useJwtAsState
 * guards it (GMA-1275): mobile Safari can hand back a null accessor during
 * page teardown, and the countdown must never throw out of the layout.
 */

const readDeadline = () => {
  try {
    const raw = window.localStorage?.getItem(SESSION_DEADLINE_KEY);
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  } catch (_err) {
    return null;
  }
};

const writeDeadline = (deadline) => {
  try {
    window.localStorage?.setItem(SESSION_DEADLINE_KEY, String(deadline));
  } catch (_err) {
    // A private-mode or torn-down storage must not break the layout. The
    // countdown then degrades to per-tab rather than shared, which is still
    // strictly better than no timer.
  }
};

/**
 * `auth_time` is a unix-seconds stamp refreshed on activity by AuthProvider.
 * It is the only record an older session leaves behind.
 */
const readLegacyAuthTimeDeadline = () => {
  try {
    const raw = window.localStorage?.getItem("auth_time");
    const seconds = Number(raw);
    if (!raw || !Number.isFinite(seconds) || seconds <= 0) {return null;}
    return seconds * 1000 + SESSION_IDLE_MS;
  } catch (_err) {
    return null;
  }
};

const clearDeadline = () => {
  try {
    window.localStorage?.removeItem(SESSION_DEADLINE_KEY);
  } catch (_err) {
    // see writeDeadline
  }
};

/**
 * Exactly one tab should perform the logout.
 *
 * `navigator.locks` is the only genuinely exclusive primitive available to us,
 * so it is used where the browser has it (every current browser does; the app
 * already asks users for Chrome). localStorage has no compare-and-swap, so the
 * fallback below is a read-then-write claim with a re-read: it closes the
 * common case but is NOT exclusive — two tabs can both read "no claim" before
 * either writes. That residual is accepted rather than hidden, because the cost
 * of losing the race is a duplicate `idle_logout` breadcrumb and a second
 * idempotent `logout()`, while the cost of failing to log out is a PHI session
 * left open on a shared workstation.
 */
/** The claim's only writer. Two spellings of this value is exactly the bug the
 *  lease check caught: a claim written without its timestamp parses as NaN and
 *  reads as an expired lease, so a live owner looks replaceable. */
const writeClaim = (tabId) => {
  window.localStorage?.setItem(
    SESSION_EXPIRY_CLAIM_KEY,
    `${tabId}|${Date.now()}`
  );
};

/**
 * The marker written once the logout has actually COMPLETED.
 *
 * A leased claim means "I am doing this, take over if I die." Re-asserting one
 * after onExpire made a finished logout indistinguishable from an owner that
 * crashed midway: a tab that processes its storage event late (suspended, or a
 * throttled background tab) would find the lease stale 10 seconds on, claim it,
 * and fire onExpire a second time - breaking the exactly-once guarantee this
 * claim exists to provide. A terminal claim is never leasable, so it survives
 * any delay.
 */
const TERMINAL_CLAIM_STAMP = "done";

const writeTerminalClaim = (tabId) => {
  window.localStorage?.setItem(
    SESSION_EXPIRY_CLAIM_KEY,
    `${tabId}|${TERMINAL_CLAIM_STAMP}`
  );
};

const claimExpiryViaStorage = (tabId) => {
  try {
    const storage = window.localStorage;
    if (!storage) {return true;}
    const existing = storage.getItem(SESSION_EXPIRY_CLAIM_KEY);
    if (existing) {
      const [owner, stamped] = String(existing).split("|");
      if (owner === tabId) {return true;}
      // A completed logout is terminal - no lease, no takeover, ever. Without
      // this the stamp parses as NaN and falls through to "stale", which is
      // exactly the second onExpire it exists to prevent.
      if (stamped === TERMINAL_CLAIM_STAMP) {return false;}
      const age = Date.now() - Number(stamped);
      // A live owner keeps it. A stale lease means that owner is gone before it
      // finished the logout, so a survivor takes over rather than leaving an
      // authenticated session open forever.
      if (Number.isFinite(age) && age < SESSION_EXPIRY_CLAIM_LEASE_MS) {
        return false;
      }
    }
    writeClaim(tabId);
    const now = storage.getItem(SESSION_EXPIRY_CLAIM_KEY);
    return String(now).split("|")[0] === tabId;
  } catch (_err) {
    return true;
  }
};

/**
 * Runs `fn` with cross-tab exclusion where the platform provides it.
 *
 * Both the reset write and the expiry check-then-clear go through this, under
 * the same name, so they cannot interleave. Earlier rounds serialized only the
 * expiry side and re-read the deadline first; that narrowed the window but did
 * not close it, because a peer's reset never took the lock at all.
 *
 * Where `navigator.locks` is absent the call runs inline — the same behaviour
 * as before, stated rather than implied. Resets are throttled to two per
 * second, so the cost of taking a lock here is not the hot path it looked like.
 */
const withDeadlineLock = (fn) => {
  const locks = typeof navigator !== "undefined" ? navigator.locks : null;
  if (locks && typeof locks.request === "function") {
    try {
      locks.request(SESSION_DEADLINE_LOCK, fn).catch(() => fn());
      return;
    } catch (_err) {
      // fall through to inline
    }
  }
  fn();
};

const releaseExpiryClaim = () => {
  try {
    window.localStorage?.removeItem(SESSION_EXPIRY_CLAIM_KEY);
  } catch (_err) {
    // see writeDeadline
  }
};

let tabCounter = 0;

export const useSessionCountdown = ({ enabled, onExpire }) => {
  const [remainingMs, setRemainingMs] = useState(null);

  const deadlineRef = useRef(null);
  const lastActivityRef = useRef(0);
  const warnedRef = useRef(false);
  const expiredRef = useRef(false);
  const onExpireRef = useRef(onExpire);
  const tabIdRef = useRef(null);
  if (tabIdRef.current === null) {
    tabCounter += 1;
    tabIdRef.current = `t${tabCounter}-${Math.random().toString(36).slice(2)}`;
  }

  // Keep the latest callback without restarting the timer on every render.
  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  const applyDeadline = useCallback((deadline) => {
    deadlineRef.current = deadline;
    const remaining = deadline - Date.now();
    if (remaining > 0) {
      warnedRef.current = isWithinWarning(remaining) ? warnedRef.current : false;
      expiredRef.current = false;
    }
    setRemainingMs(remaining);
  }, []);

  const resetDeadline = useCallback(() => {
    // Same guard handleActivity carries. Layout calls this publicly on every
    // route change, so without it a navigation at or past the deadline writes a
    // fresh 30 minutes before the delayed tick can log out.
    const now = Date.now();
    const current = deadlineRef.current ?? readDeadline();
    if (current !== null && now >= current) {
      applyDeadline(current);
      return;
    }
    const deadline = now + SESSION_IDLE_MS;
    // Local state updates synchronously so the clock never lags the user; only
    // the SHARED write is serialized against expiry.
    warnedRef.current = false;
    applyDeadline(deadline);
    withDeadlineLock(() => {
      // Re-check INSIDE the callback. The lock can be held by another tab, so
      // this runs an arbitrary time after the activity that queued it - long
      // enough for the session to have ended in between. A mousedown queues
      // this reset, the button's click then calls endSession() and clears
      // storage, and without this guard the captured deadline is written back
      // afterwards for the next login to inherit. Same reasoning as the expiry
      // branch, which re-checks for the same reason.
      if (expiredRef.current) {return;}
      // expiredRef is this TAB's state, and it is not enough. A sign-out in
      // another tab clears localStorage synchronously, but this tab's storage
      // event may not have run yet - so the ref is still false while the
      // session is already over. Publishing then recreates the deadline AND
      // releases the terminal expiry claim, letting the next login inherit the
      // old clock and letting another tab claim expiry again. Ask storage.
      try {
        if (window.localStorage?.getItem(AUTH_TOKEN_KEY) === null) {return;}
      } catch (_err) {
        // storage unreadable: publish nothing rather than guess.
        return;
      }
      writeDeadline(deadline);
      releaseExpiryClaim();
    });
  }, [applyDeadline]);

  useEffect(() => {
    if (!enabled) {
      deadlineRef.current = null;
      setRemainingMs(null);
      return undefined;
    }

    // Adopt a live deadline another tab already published; only start a fresh
    // one when there is none, or the stored one has already run out.
    const existing = readDeadline();
    const now = Date.now();
    if (existing) {
      // Adopt it whether or not it has already passed. A tab that was closed
      // or suspended leaves a stale deadline behind because no interval ran to
      // act on it; overwriting would silently revive a dead session. An
      // expired one is adopted here and the immediate tick below ends it.
      applyDeadline(existing);
    } else {
      // No countdown deadline yet. A session that started before this feature
      // carries only `auth_time`, and honouring it keeps the legacy 30-minute
      // semantics rather than silently granting a fresh window on first load -
      // which would be a security regression dressed as a migration.
      const legacy = readLegacyAuthTimeDeadline();
      const deadline = legacy !== null ? legacy : now + SESSION_IDLE_MS;
      writeDeadline(deadline);
      releaseExpiryClaim();
      applyDeadline(deadline);
    }
    // NOT `now`: that throttles away the first real activity after mount. On an
    // adopted deadline with under ACTIVITY_THROTTLE_MS left, a user who clicks
    // immediately would be ignored and signed out despite acting. Start ready.
    lastActivityRef.current = 0;
    warnedRef.current = false;
    expiredRef.current = false;

    addBreadcrumb("auth.session.countdown_started", {
      category: "auth",
      level: "info",
      idleMs: SESSION_IDLE_MS,
    });

    const handleActivity = () => {
      if (expiredRef.current) {return;}
      const at = Date.now();
      // The interval may not have run yet - browser timer throttling and laptop
      // sleep both delay it. Activity past the deadline must not push it out;
      // the next tick completes the logout.
      if (deadlineRef.current !== null && at >= deadlineRef.current) {
        setRemainingMs(deadlineRef.current - at);
        return;
      }
      if (at - lastActivityRef.current < ACTIVITY_THROTTLE_MS) {return;}
      lastActivityRef.current = at;
      resetDeadline();
    };

    const handleStorage = (event) => {
      // key === null is localStorage.clear() from another tab, which is what
      // logout() does. The session is over; stop counting and do not log out
      // again from here.
      if (event.key === null) {
        // Another tab cleared storage. Two very different causes land here:
        // an idle logout, or someone pressing Sign out. `markIdleLogout` writes
        // per-tab sessionStorage, so only the tab that ran the logout had the
        // explanation - every other tab was redirected to /login by
        // useJwtAsState and arrived with no reason shown. Mark it here too, but
        // ONLY when this tab's own clock had actually run out; claiming an idle
        // sign-out after a manual one would be a lie to the user.
        //
        // Issue #672: the ELSE was the fourth silent path. A peer tab signing
        // out (or its refresh failing) left this tab's own clock with time on
        // it, so nothing was marked and this tab hard-navigated to /login with
        // no explanation — worst of all from /call, which is outside
        // RequireLogin and so never reaches the route guard's marker. "Your
        // session ended" is exactly right here: from THIS tab's point of view
        // it did, and the user did nothing in this tab to cause it.
        const deadline = deadlineRef.current;
        if (deadline !== null && Date.now() >= deadline) {
          markIdleLogout();
        } else {
          markSessionEndReasonIfUnset(SESSION_END_REASON_SESSION_ENDED);
        }
        expiredRef.current = true;
        deadlineRef.current = null;
        setRemainingMs(null);
        // Stopping the clock is not signing out. Most surfaces are unmounted by
        // RequireLogin once useJwtAsState notices the token is gone - but the
        // standalone /call route is NOT wrapped by it, so a losing tab would
        // keep a live call, and the client data on it, on screen after the
        // session ended. Tell those surfaces to tear down, then leave.
        // Await the teardown before navigating: the call page ends the call
        // with an ordinary fetch, and navigating aborts an in-flight request,
        // leaving the call ALIVE on the server. Bounded, so a surface that
        // hangs cannot hold the sign-out open.
        emitSessionEndAndSettle().then(() => {
          // Issue #637 — the same announcement the idle path makes, for the
          // same reason: this navigation kills in-flight polls, and WebKit
          // reports those deaths as "TypeError: Load failed", which is its
          // wording for a real outage too. After the teardown settles, so the
          // teardown's own request stays reportable.
          markTeardown();
          try {
            // REPLACE, never assign href - see AuthContext's idle path. /call is
            // outside RequireLogin, so a pushed history entry survives the
            // sign-out and Back can restore it.
            window.location.replace("/login");
          } catch (_err) {
            // jsdom and any environment without navigation; the surfaces have
            // already been told to close.
          }
        });
        return;
      }
      if (event.key !== SESSION_DEADLINE_KEY) {return;}
      const next = Number(event.newValue);
      if (!Number.isFinite(next) || next <= 0) {return;}
      // Another tab saw activity. Adopt its deadline; never write back, or two
      // tabs would ping-pong a storage event at each other.
      if (next > Date.now()) {
        warnedRef.current = false;
        expiredRef.current = false;
      }
      applyDeadline(next);
    };

    const tick = () => {
      const deadline = deadlineRef.current;
      if (deadline === null) {return;}
      const remaining = deadline - Date.now();
      setRemainingMs(remaining);

      if (remaining <= 0) {
        if (expiredRef.current) {return;}

        // Storage events are asynchronous, so this tab can arrive here holding a
        // deadline another tab has already reset. Re-read the authoritative
        // stored value first: signing everyone out moments after real activity
        // is the worst outcome this countdown can produce.
        const stored = readDeadline();
        if (stored !== null && stored > Date.now()) {
          warnedRef.current = false;
          applyDeadline(stored);
          return;
        }


        // A throttled or slept timer can jump straight past the whole warning
        // band. The release filter asserts a warning always precedes a logout,
        // so record the one that was skipped rather than dropping it - a
        // missing warning is supposed to mean the countdown never rendered.
        if (!warnedRef.current) {
          warnedRef.current = true;
          addBreadcrumb("auth.session.countdown_warning", {
            category: "auth",
            level: "info",
            remainingMs: remaining,
            skipped: true,
          });
        }

        // Do NOT null the deadline on a loss. The winner may never finish, and
        // a tab that has permanently stopped cannot recover the session; keep
        // ticking so the lease above can be taken over.
        withDeadlineLock(() => {
          // The guard on the way IN is not enough. navigator.locks.request is
          // asynchronous, so each expired tick queues a callback while the lock
          // is held; the first sets expiredRef and logs out, and every one
          // behind it would find its own tab already holding the claim and log
          // out again. Re-check here, where the decision is actually made.
          if (expiredRef.current) {
            return;
          }

          // Re-read INSIDE the lock. Outside it, a peer's reset could land
          // between this check and the clear below — which is precisely the
          // window the previous, expiry-only serialization left open.
          const fresh = readDeadline();
          if (fresh !== null && fresh > Date.now()) {
            warnedRef.current = false;
            applyDeadline(fresh);
            return;
          }
          if (!claimExpiryViaStorage(tabIdRef.current)) {
            return;
          }
          expiredRef.current = true;
          clearDeadline();
          addBreadcrumb("auth.session.idle_logout", {
            category: "auth",
            level: "warning",
            idleMs: SESSION_IDLE_MS,
          });
          if (typeof onExpireRef.current === "function") {
            onExpireRef.current();
          }
          // The real onExpire is AuthContext.logout(), which runs
          // localStorage.clear() and takes the claim with it. A tab ticking
          // before the storage event arrives would then see no claim and log
          // out again, so re-assert it after the callback returns - as a
          // TERMINAL claim, because the logout is finished. A fresh lease here
          // would be recoverable by a late-waking tab and fire onExpire twice.
          try {
            writeTerminalClaim(tabIdRef.current);
          } catch (_err) {
            // storage gone entirely; the storage-event handler still covers it
          }
        });
        return;
      }

      if (isWithinWarning(remaining) && !warnedRef.current) {
        warnedRef.current = true;
        addBreadcrumb("auth.session.countdown_warning", {
          category: "auth",
          level: "info",
          remainingMs: remaining,
        });
      }
    };

    const intervalId = setInterval(tick, TICK_MS);
    // Run once immediately: an adopted deadline may already have passed, and
    // waiting a full tick would leave a dead session rendered as live.
    tick();
    ACTIVITY_EVENTS.forEach((name) =>
      window.addEventListener(name, handleActivity, { passive: true })
    );
    window.addEventListener("storage", handleStorage);

    return () => {
      clearInterval(intervalId);
      ACTIVITY_EVENTS.forEach((name) =>
        window.removeEventListener(name, handleActivity)
      );
      window.removeEventListener("storage", handleStorage);
    };
  }, [enabled, applyDeadline, resetDeadline]);

  // Called synchronously by logout(), during the same click dispatch, so the
  // window activity listener that is still attached cannot resurrect the
  // deadline. Clearing the key here too means a logout path that does not empty
  // all of localStorage still leaves no clock behind.
  const endSession = useCallback(() => {
    // Before anything else: a page blocking navigation must stop blocking it.
    // The call page's beforeunload guard would otherwise turn the redirect to
    // /login into a prompt the user can cancel, stranding a signed-out session
    // on screen with no retry. The returned promise settles once the surfaces
    // have finished tearing down, so the caller can clear credentials and
    // navigate AFTER the teardown request has gone, not during it.
    const teardown = emitSessionEndAndSettle();
    // expiredRef is what makes this stick: a manual sign-out is a CLICK, and
    // that click keeps bubbling to the window activity listener after
    // logout() has emptied storage, because the listener is only detached on
    // the re-render that follows. handleActivity bails on expiredRef, so the
    // sign-out click can no longer write the deadline back into the storage
    // logout just cleared - which the next login would otherwise inherit.
    expiredRef.current = true;
    deadlineRef.current = null;
    setRemainingMs(null);
    try {
      window.localStorage.removeItem(SESSION_DEADLINE_KEY);
    } catch {
      // storage unavailable (private mode, quota) - nothing to clean up
    }
    return teardown;
  }, []);

  return {
    remainingMs,
    isWarning: isWithinWarning(remainingMs),
    resetDeadline,
    endSession,
  };
};

export default useSessionCountdown;
